import { ReactFitty } from 'react-fitty';

import { GameInstance, GameMenuItem } from '../../../api/menu/GameMenuItem';
import { useGetGameMenuItemQuery } from '../../../api/menu/menuSlice';

import { getAvailabilityInfo } from './gameAvailability';

interface Props {
  menuItemId: number;
  onCancel: () => void;
  onSelect: (instance: GameInstance, gameMenuItem: GameMenuItem) => void;
}

export default function GameInstancePicker({ menuItemId, onCancel, onSelect }: Props) {
  const { data: gameMenuItem, error } = useGetGameMenuItemQuery(menuItemId, {
    skip: !menuItemId,
  });

  if (error) {
    return (
      <div className="pos-modal-portal">
        <div className="pos-modal-overlay" onClick={onCancel}>
          <div className="pos-modal pos-modal_role_item">
            <div className="pos-modal__content">
              <div className="pos-item js-item">
                <div className="pos-item__header">
                  <div className="pos-item__info">
                    <h2 className="pos-item__title">Could not load game</h2>
                  </div>
                </div>
                <div className="pos-item__meta">
                  <button className="pos-item__add-button" onClick={onCancel}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!gameMenuItem) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((e.target as any).className === 'pos-modal-overlay') onCancel();
  };

  const handleInstanceClick = (instance: GameInstance) => {
    if (!instance.isSellable) return;
    const availabilityInfo = getAvailabilityInfo(instance);
    if (!availabilityInfo.available) return;
    onSelect(instance, gameMenuItem);
  };

  const fittyDims = () => {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    let maxWidth = '430px';

    if (windowHeight === 800 && windowWidth === 1280) maxWidth = '473px';
    else if (windowHeight === 768 && windowWidth === 1024) maxWidth = '300px';
    else if (windowHeight === 600 && windowWidth === 1024) maxWidth = '300px';

    if (windowHeight >= 900 && windowHeight <= 905) return { min: 30, max: 50, width: maxWidth };
    if (windowHeight == 800) return { min: 30, max: 45, width: maxWidth };
    if (windowHeight == 768) return { min: 25, max: 35, width: maxWidth };
    if (windowHeight >= 600 && windowHeight <= 605) return { min: 25, max: 35, width: maxWidth };
    return { min: 30, max: 60, width: maxWidth };
  };

  const InstanceButton = ({ position }: { position: number }) => {
    const instance = gameMenuItem.instances[position - 1];
    if (!instance) return <div className="pos-item__condiment hidden-button"></div>;

    const availabilityInfo = getAvailabilityInfo(instance);

    const getAvailabilityText = () => {
      if (!instance.isSellable || !availabilityInfo.available) return 'Not Available';
      if (availabilityInfo.timeUntilNextBooking === undefined) return 'Available';
      return `${availabilityInfo.timeUntilNextBooking} ${gameMenuItem.timeQuantifier}${
        availabilityInfo.timeUntilNextBooking !== 1 ? 's' : ''
      } until next booking`;
    };

    return (
      <div
        className={`pos-item__condiment ${!instance.isSellable || !availabilityInfo.available ? 'is-disabled' : ''}`}
        onClick={() => handleInstanceClick(instance)}
      >
        <p className="pos-item__condiment-text">{instance.instanceName}</p>
        <p className="pos-item__condiment-price">{getAvailabilityText()}</p>
      </div>
    );
  };

  return (
    <div className="pos-modal-portal">
      <div className="pos-modal-overlay" onClick={handleOverlayClick}>
        <div className="pos-modal pos-modal_role_item">
          <div className="pos-modal__content">
            <div className="pos-item js-item">
              <div className="pos-item__header">
                <div className="pos-item__info">
                  <h2 className="pos-item__title">
                    <span className="pos-item__title-text js-item-title-text">
                      <ReactFitty style={{ width: fittyDims().width }} minSize={fittyDims().min} maxSize={fittyDims().max}>
                        {gameMenuItem.instanceName}
                      </ReactFitty>
                    </span>
                  </h2>
                </div>
              </div>

              <div className="pos-item__options js-item-options">
                <div className="pos-item__condiments">
                  <div className="pos-item__condiments-row">
                    <InstanceButton position={1} />
                    <InstanceButton position={2} />
                    <InstanceButton position={3} />
                    <InstanceButton position={4} />
                  </div>
                  <div className="pos-item__condiments-row">
                    <InstanceButton position={5} />
                    <InstanceButton position={6} />
                    <InstanceButton position={7} />
                    <InstanceButton position={8} />
                  </div>
                  <div className="pos-item__condiments-row">
                    <InstanceButton position={9} />
                    <InstanceButton position={10} />
                    <InstanceButton position={11} />
                    <InstanceButton position={12} />
                  </div>
                  <div className="pos-item__condiments-row">
                    <InstanceButton position={13} />
                    <InstanceButton position={14} />
                    <InstanceButton position={15} />
                    <InstanceButton position={16} />
                  </div>
                </div>
              </div>

              <div className="pos-item__meta">
                <p className="pos-item__meta-text">
                  <button className="pos-item__add-button" onClick={onCancel}>
                    Cancel
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
